import { useState, useEffect, useCallback } from 'react';
import { Card, Row, Col, Table, Badge, Button, Spinner, Alert, Form, OverlayTrigger, Tooltip } from 'react-bootstrap';
import { toast } from 'react-toastify';

const FUNCTIONS_URL = process.env.REACT_APP_FIREBASE_FUNCTIONS_URL || 'https://us-central1-stk-merlin.cloudfunctions.net';

/**
 * Smart Sales Cliffs Component
 * Displays FC change alerts with:
 * - Adaptive detection windows based on velocity tier
 * - Statistical significance testing
 * - Financial risk scoring
 */
export default function SalesCliffs() {
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [alerts, setAlerts] = useState([]);
  const [summary, setSummary] = useState({
    total: 0,
    byAction: { REVERT: 0, REDUCE: 0, MONITOR: 0, INVESTIGATE: 0 },
    byPriority: { URGENT: 0, HIGH: 0, MEDIUM: 0 },
    byRiskLevel: { CRITICAL: 0, HIGH: 0, MEDIUM: 0, LOW: 0 },
    totalExposure: 0
  });
  const [statusFilter, setStatusFilter] = useState('NEW');
  const [detecting, setDetecting] = useState(false);
  const [buildingProfiles, setBuildingProfiles] = useState(false);

  // Fetch alerts
  const fetchAlerts = useCallback(async () => {
    setLoading(true);
    setError(null);

    try {
      const response = await fetch(
        `${FUNCTIONS_URL}/fcSalesCliffAlerts?status=${statusFilter}&limit=100`
      );

      if (!response.ok) {
        throw new Error(`HTTP ${response.status}: ${response.statusText}`);
      }

      const result = await response.json();

      if (result.success) {
        setAlerts(result.alerts || []);
        setSummary(result.summary || {
          total: 0,
          byAction: { REVERT: 0, REDUCE: 0, MONITOR: 0, INVESTIGATE: 0 },
          byPriority: { URGENT: 0, HIGH: 0, MEDIUM: 0 }
        });
      } else {
        throw new Error(result.message || 'Failed to fetch alerts');
      }
    } catch (err) {
      console.error('Error fetching sales cliff alerts:', err);
      setError(err.message);
    } finally {
      setLoading(false);
    }
  }, [statusFilter]);

  useEffect(() => {
    fetchAlerts();
  }, [fetchAlerts]);

  // Run detection manually
  const handleRunDetection = async (forceAll = false) => {
    setDetecting(true);
    toast.info('Running smart sales cliff detection...');

    try {
      const response = await fetch(`${FUNCTIONS_URL}/fcSalesCliffDetect`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ forceAll, rebuildProfiles: false })
      });

      const result = await response.json();

      if (result.success) {
        const riskSummary = result.byRiskLevel
          ? ` (Critical: ${result.byRiskLevel.CRITICAL || 0}, High: ${result.byRiskLevel.HIGH || 0})`
          : '';
        toast.success(
          `Detection complete! Analyzed ${result.analyzed} FC changes. ` +
          `Found ${result.alerts?.length || 0} new alerts${riskSummary}.`
        );
        fetchAlerts(); // Refresh
      } else {
        toast.error(`Detection failed: ${result.message || 'Unknown error'}`);
      }
    } catch (err) {
      console.error('Error running detection:', err);
      toast.error('Failed to run detection. Check console for details.');
    } finally {
      setDetecting(false);
    }
  };

  // Build velocity profiles
  const handleBuildProfiles = async () => {
    setBuildingProfiles(true);
    toast.info('Building SKU velocity profiles...');

    try {
      const response = await fetch(`${FUNCTIONS_URL}/fcBuildVelocityProfiles`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ limit: 500, forceRefresh: true })
      });

      const result = await response.json();

      if (result.success) {
        toast.success(
          `Profiled ${result.profiled} SKUs. ` +
          `Very High: ${result.byTier?.VERY_HIGH || 0}, High: ${result.byTier?.HIGH || 0}, ` +
          `Medium: ${result.byTier?.MEDIUM || 0}, Low: ${result.byTier?.LOW || 0}`
        );
      } else {
        toast.error(`Profile build failed: ${result.message || 'Unknown error'}`);
      }
    } catch (err) {
      console.error('Error building profiles:', err);
      toast.error('Failed to build velocity profiles.');
    } finally {
      setBuildingProfiles(false);
    }
  };

  // Update alert status
  const handleUpdateStatus = async (alertId, newStatus) => {
    try {
      const response = await fetch(`${FUNCTIONS_URL}/fcSalesCliffUpdate`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ alertId, status: newStatus })
      });

      const result = await response.json();

      if (result.success) {
        toast.success(`Alert marked as ${newStatus}`);
        fetchAlerts(); // Refresh
      } else {
        toast.error(`Update failed: ${result.message || 'Unknown error'}`);
      }
    } catch (err) {
      console.error('Error updating alert:', err);
      toast.error('Failed to update alert status');
    }
  };

  // Format velocity
  const formatVelocity = (velocity) => {
    if (velocity === null || velocity === undefined) return '-';
    return `${velocity.toFixed(2)}/day`;
  };

  // Format date
  const formatDate = (date) => {
    if (!date) return '-';
    const d = new Date(date);
    return d.toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' });
  };

  // Get badge variant for priority
  const getPriorityVariant = (priority) => {
    switch (priority) {
      case 'URGENT': return 'danger';
      case 'HIGH': return 'warning';
      case 'MEDIUM': return 'info';
      default: return 'secondary';
    }
  };

  // Get badge variant for action
  const getActionVariant = (action) => {
    switch (action) {
      case 'REVERT': return 'danger';
      case 'REDUCE': return 'warning';
      case 'MONITOR': return 'info';
      case 'INVESTIGATE': return 'primary';
      default: return 'secondary';
    }
  };

  // Get badge variant for risk level
  const getRiskVariant = (risk) => {
    switch (risk) {
      case 'CRITICAL': return 'danger';
      case 'HIGH': return 'warning';
      case 'MEDIUM': return 'info';
      default: return 'secondary';
    }
  };

  return (
    <div className="p-3">
      {/* Summary Section - Action Cards */}
      <Row className="mb-2">
        <Col md={3}>
          <Card className="h-100 border-danger">
            <Card.Body className="text-center py-2">
              <h6 className="text-danger mb-1">REVERT</h6>
              <h3 className="mb-0">{summary.byAction?.REVERT || 0}</h3>
              <small className="text-muted">Sales dropped &gt;70%</small>
            </Card.Body>
          </Card>
        </Col>
        <Col md={3}>
          <Card className="h-100 border-warning">
            <Card.Body className="text-center py-2">
              <h6 className="text-warning mb-1">REDUCE</h6>
              <h3 className="mb-0">{summary.byAction?.REDUCE || 0}</h3>
              <small className="text-muted">Sales dropped 50-70%</small>
            </Card.Body>
          </Card>
        </Col>
        <Col md={3}>
          <Card className="h-100 border-info">
            <Card.Body className="text-center py-2">
              <h6 className="text-info mb-1">MONITOR</h6>
              <h3 className="mb-0">{summary.byAction?.MONITOR || 0}</h3>
              <small className="text-muted">Sales dropped 30-50%</small>
            </Card.Body>
          </Card>
        </Col>
        <Col md={3}>
          <Card className="h-100 border-primary">
            <Card.Body className="text-center py-2">
              <h6 className="text-primary mb-1">INVESTIGATE</h6>
              <h3 className="mb-0">{summary.byAction?.INVESTIGATE || 0}</h3>
              <small className="text-muted">Unusual patterns</small>
            </Card.Body>
          </Card>
        </Col>
      </Row>

      {/* Financial Risk Summary */}
      {summary.totalExposure > 0 && (
        <Alert variant="warning" className="mb-3 py-2">
          <Row className="align-items-center">
            <Col>
              <strong>Total Financial Exposure:</strong>{' '}
              <span className="text-danger fs-5">${summary.totalExposure?.toFixed(2) || '0.00'}</span>
            </Col>
            <Col xs="auto">
              <Badge bg="danger" className="me-2">Critical: {summary.byRiskLevel?.CRITICAL || 0}</Badge>
              <Badge bg="warning" className="me-2">High: {summary.byRiskLevel?.HIGH || 0}</Badge>
              <Badge bg="info">Medium: {summary.byRiskLevel?.MEDIUM || 0}</Badge>
            </Col>
          </Row>
        </Alert>
      )}

      {/* Controls */}
      <Row className="mb-3 align-items-center">
        <Col>
          <Form.Select
            size="sm"
            value={statusFilter}
            onChange={(e) => setStatusFilter(e.target.value)}
            style={{ width: 'auto', display: 'inline-block' }}
          >
            <option value="NEW">New Alerts</option>
            <option value="ACKNOWLEDGED">Acknowledged</option>
            <option value="RESOLVED">Resolved</option>
            <option value="all">All Alerts</option>
          </Form.Select>
        </Col>
        <Col xs="auto">
          <OverlayTrigger
            placement="top"
            overlay={<Tooltip>Build velocity profiles for SKUs (needed for smart detection)</Tooltip>}
          >
            <Button
              variant="outline-info"
              size="sm"
              className="me-2"
              onClick={handleBuildProfiles}
              disabled={buildingProfiles}
            >
              {buildingProfiles ? (
                <>
                  <Spinner animation="border" size="sm" className="me-1" />
                  Building...
                </>
              ) : (
                'Build Profiles'
              )}
            </Button>
          </OverlayTrigger>
          <OverlayTrigger
            placement="top"
            overlay={<Tooltip>Run smart detection with adaptive windows and statistical testing</Tooltip>}
          >
            <Button
              variant="outline-warning"
              size="sm"
              onClick={() => handleRunDetection(false)}
              disabled={detecting}
            >
              {detecting ? (
                <>
                  <Spinner animation="border" size="sm" className="me-1" />
                  Detecting...
                </>
              ) : (
                'Run Detection'
              )}
            </Button>
          </OverlayTrigger>
          <Button
            variant="outline-secondary"
            size="sm"
            className="ms-2"
            onClick={fetchAlerts}
            disabled={loading}
          >
            {loading ? <Spinner animation="border" size="sm" /> : 'Refresh'}
          </Button>
        </Col>
      </Row>

      {/* Error Alert */}
      {error && (
        <Alert variant="danger" className="mb-3">
          <strong>Error:</strong> {error}
        </Alert>
      )}

      {/* Info Alert when no alerts */}
      {!loading && !error && alerts.length === 0 && (
        <Alert variant="info">
          <strong>No sales cliff alerts found.</strong>
          <p className="mb-2 fs--1 mt-2">
            <strong>Smart Detection</strong> runs <strong>hourly</strong> with adaptive timing based on each SKU's velocity:
          </p>
          <ul className="mb-2 fs--1">
            <li><strong>Very High velocity (50+/day)</strong>: Checked after 12 hours</li>
            <li><strong>High velocity (10-50/day)</strong>: Checked after 2 days</li>
            <li><strong>Medium velocity (1-10/day)</strong>: Checked after 5 days</li>
            <li><strong>Low velocity (&lt;1/day)</strong>: Checked after 14-21 days</li>
          </ul>
          <p className="mb-0 fs--1">
            Alerts only appear when drops are <strong>statistically significant</strong> and include <strong>financial risk scoring</strong> to prioritize your attention.
            Click "Build Profiles" to analyze your SKU velocities, then "Run Detection" to check for alerts.
          </p>
        </Alert>
      )}

      {/* Alerts Table */}
      {loading ? (
        <div className="text-center py-5">
          <Spinner animation="border" />
          <p className="mt-2 text-muted">Loading alerts...</p>
        </div>
      ) : alerts.length > 0 ? (
        <div className="table-responsive">
          <Table hover className="fs--1 mb-0">
            <thead className="bg-200">
              <tr>
                <th>Risk</th>
                <th>Action</th>
                <th>SKU</th>
                <th>FC Change</th>
                <th>Velocity</th>
                <th>Drop %</th>
                <th>Confidence</th>
                <th>Exposure</th>
                <th>Days</th>
                <th>Actions</th>
              </tr>
            </thead>
            <tbody>
              {alerts.map((alert) => (
                <tr key={alert.id} className={
                  alert.financialRisk === 'CRITICAL' ? 'table-danger' :
                  alert.financialRisk === 'HIGH' ? 'table-warning' : ''
                }>
                  <td>
                    <Badge bg={getRiskVariant(alert.financialRisk)}>
                      {alert.financialRisk || 'N/A'}
                    </Badge>
                  </td>
                  <td>
                    <Badge bg={getActionVariant(alert.action)}>
                      {alert.action}
                    </Badge>
                  </td>
                  <td className="fw-semi-bold">{alert.sku}</td>
                  <td>
                    <span className="text-muted">${alert.oldFC?.toFixed(2) || '-'}</span>
                    <span className="mx-1">&rarr;</span>
                    <span className="fw-semi-bold">${alert.newFC?.toFixed(2) || '-'}</span>
                    {alert.fcDelta > 0 && (
                      <span className="text-danger ms-1">(+${alert.fcDelta?.toFixed(2)})</span>
                    )}
                  </td>
                  <td>
                    <OverlayTrigger
                      placement="top"
                      overlay={
                        <Tooltip>
                          Before: {formatVelocity(alert.preVelocity)}<br />
                          After: {formatVelocity(alert.postVelocity)}
                        </Tooltip>
                      }
                    >
                      <span>
                        {formatVelocity(alert.preVelocity)} &rarr; {formatVelocity(alert.postVelocity)}
                      </span>
                    </OverlayTrigger>
                  </td>
                  <td>
                    <span className={alert.velocityChangePercent < 0 ? 'text-danger fw-bold' : 'text-success'}>
                      {alert.velocityChangePercent > 0 && '+'}
                      {alert.velocityChangePercent?.toFixed(0) || 0}%
                    </span>
                  </td>
                  <td>
                    <Badge bg={
                      alert.statisticalConfidence === 'HIGH' ? 'success' :
                      alert.statisticalConfidence === 'MEDIUM' ? 'warning' : 'secondary'
                    }>
                      {alert.statisticalConfidence || 'N/A'}
                    </Badge>
                  </td>
                  <td>
                    <OverlayTrigger
                      placement="top"
                      overlay={
                        <Tooltip>
                          Daily: ${alert.dailyExposure?.toFixed(2) || '0.00'}<br />
                          Cumulative: ${alert.cumulativeExposure?.toFixed(2) || '0.00'}
                        </Tooltip>
                      }
                    >
                      <span className={alert.cumulativeExposure > 100 ? 'text-danger fw-bold' : ''}>
                        ${alert.cumulativeExposure?.toFixed(0) || '0'}
                      </span>
                    </OverlayTrigger>
                  </td>
                  <td>{alert.daysSinceChange || '-'}</td>
                  <td>
                    {alert.status === 'NEW' && (
                      <>
                        <Button
                          variant="outline-primary"
                          size="sm"
                          className="me-1"
                          onClick={() => handleUpdateStatus(alert.id, 'ACKNOWLEDGED')}
                          title="Mark as acknowledged"
                        >
                          Ack
                        </Button>
                        <Button
                          variant="outline-success"
                          size="sm"
                          onClick={() => handleUpdateStatus(alert.id, 'RESOLVED')}
                          title="Mark as resolved"
                        >
                          Resolve
                        </Button>
                      </>
                    )}
                    {alert.status === 'ACKNOWLEDGED' && (
                      <Button
                        variant="outline-success"
                        size="sm"
                        onClick={() => handleUpdateStatus(alert.id, 'RESOLVED')}
                        title="Mark as resolved"
                      >
                        Resolve
                      </Button>
                    )}
                    {alert.status === 'RESOLVED' && (
                      <Badge bg="success">Resolved</Badge>
                    )}
                  </td>
                </tr>
              ))}
            </tbody>
          </Table>
        </div>
      ) : null}

      {/* Legend */}
      {alerts.length > 0 && (
        <div className="mt-3 p-2 bg-100 rounded fs--1">
          <Row>
            <Col md={6}>
              <strong>Action:</strong>
              <span className="ms-2"><Badge bg="danger">REVERT</Badge> &gt;70% drop</span>
              <span className="ms-2"><Badge bg="warning">REDUCE</Badge> 50-70%</span>
              <span className="ms-2"><Badge bg="info">MONITOR</Badge> 30-50%</span>
              <span className="ms-2"><Badge bg="primary">INVESTIGATE</Badge> Unusual</span>
            </Col>
            <Col md={6}>
              <strong>Risk:</strong>
              <span className="ms-2"><Badge bg="danger">CRITICAL</Badge> &gt;$500</span>
              <span className="ms-2"><Badge bg="warning">HIGH</Badge> $100-500</span>
              <span className="ms-2"><Badge bg="info">MEDIUM</Badge> $25-100</span>
              <span className="ms-2"><Badge bg="secondary">LOW</Badge> &lt;$25</span>
            </Col>
          </Row>
        </div>
      )}
    </div>
  );
}
